import { create } from 'zustand';
import { DEFAULT_CURRENCY, zPublicCurrency, type CurrencyProfile, type CurrencyLocales } from '@tg/shared';
import { api } from '@/api/client';
import { useLocale } from '@/i18n/useLocale';

interface CurrencyStore {
  active: CurrencyProfile;
  byLocale: CurrencyLocales;
  revision: number;
  loading: boolean;
  apply(active: CurrencyProfile, revision: number, byLocale?: CurrencyLocales): void;
  load(): Promise<void>;
}
export const useCurrency = create<CurrencyStore>((set, get) => ({
  active: DEFAULT_CURRENCY,
  byLocale: {},
  revision: -1,
  loading: false,
  apply(active, revision, byLocale = {}) {
    if (revision >= get().revision) set({ active, revision, byLocale });
  },
  async load() {
    if (get().loading) return;
    set({ loading: true });
    try {
      const result = zPublicCurrency.parse(
        await api.get('/api/currency', { cache: 'no-store', signal: AbortSignal.timeout(5000) }),
      );
      get().apply(result.active, result.revision, result.activeByLocale);
    } catch {
      /* Keep the last known presentation; an unavailable setting never blocks gameplay. */
    } finally {
      set({ loading: false });
    }
  },
}));

export function useDisplayCurrency(): CurrencyProfile {
  const locale = useLocale();
  return useCurrency((s) => s.byLocale[locale] ?? s.active);
}
